/**
 * Date range utilities for dashboard KPI comparisons.
 * Compares selected date range against the immediately preceding period of equal length.
 */

const LOCAL_DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})/

/**
 * Parse a Date or `YYYY-MM-DD` value as a local calendar day (midnight).
 * Date-only strings must not go through `new Date("YYYY-MM-DD")` (UTC),
 * which shifts the day backward in US timezones.
 */
export function parseLocalDateInput(value) {
  if (value == null || value === '') return null

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }

  const raw = String(value).trim()
  const match = LOCAL_DATE_KEY.exec(raw)
  if (match) {
    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    const date = new Date(year, month - 1, day)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null
    }
    return date
  }

  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return null
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

/** `YYYY-MM-DD` for the local calendar day of `value`. */
export function formatLocalDateKey(value) {
  const date = parseLocalDateInput(value)
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function startOfLocalDay(value = new Date()) {
  return parseLocalDateInput(value) ?? parseLocalDateInput(new Date())
}

export function endOfLocalDay(value = new Date()) {
  const date = startOfLocalDay(value)
  date.setHours(23, 59, 59, 999)
  return date
}

export function localDateTodayKey() {
  return formatLocalDateKey(new Date())
}

/**
 * Returns the previous period of equal length, immediately before the given range.
 * Ranges are treated as inclusive [start, end] at day granularity.
 *
 * @param {Object} range - { start: Date, end: Date }
 * @returns {{ start: Date, end: Date } | null} Previous period, or null if invalid
 *
 * @example
 * // Last 30 days (Jan 13 - Feb 11) → previous 30 days (Dec 14 - Jan 12)
 * getPreviousPeriod({ start: Jan 13, end: Feb 11 })
 * // → { start: Dec 14, end: Jan 12 }
 */
export function getPreviousPeriod(range) {
  if (!range?.start || !range?.end) return null

  const start = parseLocalDateInput(range.start)
  const end = parseLocalDateInput(range.end)

  if (!start || !end) return null
  if (end < start) return null

  const lengthMs = end.getTime() - start.getTime()
  const lengthDays = Math.round(lengthMs / (24 * 60 * 60 * 1000)) + 1
  if (lengthDays < 1) return null

  const prevEnd = new Date(start)
  prevEnd.setDate(prevEnd.getDate() - 1)

  const prevStart = new Date(prevEnd)
  prevStart.setDate(prevStart.getDate() - (lengthDays - 1))

  return { start: prevStart, end: prevEnd }
}

/**
 * Formats a date range for tooltip display: "MMM d – MMM d"
 * Matches the Date filter formatting.
 *
 * @param {{ start: Date, end: Date } | null} range
 * @returns {string} Formatted string or empty
 */
export function formatPreviousPeriodTooltip(range) {
  if (!range?.start || !range?.end) return ''
  const start = parseLocalDateInput(range.start)
  const end = parseLocalDateInput(range.end)
  if (!start || !end) return ''
  const opts = { month: 'short', day: 'numeric' }
  return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`
}

/**
 * Calendar day immediately before the end date of the selected range (typical KPI baseline: vs previous day).
 * Uses local date of `range.end`.
 *
 * @param {{ start?: Date, end: Date | string } | null} range
 * @returns {{ start: Date, end: Date } | null}
 */
export function getPreviousDayBeforeRangeEnd(range) {
  if (!range?.end) return null
  const endDay = parseLocalDateInput(range.end)
  if (!endDay) return null
  endDay.setDate(endDay.getDate() - 1)
  return { start: endDay, end: endDay }
}

/**
 * @param {{ start: Date, end?: Date } | null} dayRange - single-day range from getPreviousDayBeforeRangeEnd
 * @returns {string}
 */
export function formatPreviousDayTooltip(dayRange) {
  if (!dayRange?.start) return ''
  const d = parseLocalDateInput(dayRange.start)
  if (!d) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
